import React from "react";

/**
 * Reemplazos de los pocos componentes de MUI que usaba el proyecto, hechos con
 * las clases de Bootstrap que ya carga la app.
 *
 * Se conservan los nombres y las props de MUI (`variant`, `color`, `size`,
 * `severity`, `elevation`, `sx`) a propósito: así los archivos que los usaban
 * sólo cambian el import y no hay que tocar 50 llamadas. `sx` sólo soporta el
 * puñado de atajos que el proyecto usaba (`ml`, `padding`).
 */

const COLOR_A_BOOTSTRAP = {
  primary: "primary",
  secondary: "secondary",
  success: "success",
  error: "danger",
  warning: "warning",
  info: "info",
  inherit: "secondary",
};

const TAMANO_A_BOOTSTRAP = {
  small: "btn-sm",
  medium: "",
  md: "",
  large: "btn-lg",
};

/** Traduce el subconjunto de `sx` que se usaba a estilos en línea. */
const sxAEstilo = (sx = {}) => {
  const estilo = {};

  if (sx.ml !== undefined) estilo.marginLeft = `${sx.ml * 0.5}rem`;
  if (sx.mr !== undefined) estilo.marginRight = `${sx.mr * 0.5}rem`;
  if (sx.mt !== undefined) estilo.marginTop = `${sx.mt * 0.5}rem`;
  if (sx.mb !== undefined) estilo.marginBottom = `${sx.mb * 0.5}rem`;
  if (sx.padding !== undefined) estilo.padding = sx.padding;
  if (sx.width !== undefined) estilo.width = sx.width;

  return estilo;
};

export function Button({
  children,
  variant = "contained",
  color = "primary",
  size = "medium",
  sx,
  style,
  className = "",
  ...resto
}) {
  const tono = COLOR_A_BOOTSTRAP[color] || "primary";
  const relleno = variant === "outlined" || variant === "text" ? "outline-" : "";

  const clases = [
    "btn",
    variant === "text" ? "btn-link" : `btn-${relleno}${tono}`,
    TAMANO_A_BOOTSTRAP[size] || "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={clases}
      style={{ ...sxAEstilo(sx), ...style }}
      {...resto}
    >
      {children}
    </button>
  );
}

const SEVERIDAD_A_BOOTSTRAP = {
  success: "success",
  info: "info",
  warning: "warning",
  error: "danger",
};

export function Alert({
  children,
  severity = "info",
  style,
  className = "",
  ...resto
}) {
  const tono = SEVERIDAD_A_BOOTSTRAP[severity] || "info";

  return (
    <div className={`alert alert-${tono} ${className}`.trim()} style={style} {...resto}>
      {children}
    </div>
  );
}

export function AlertTitle({ children, className = "", ...resto }) {
  return (
    <div className={`alert-heading fw-bold mb-1 ${className}`.trim()} {...resto}>
      {children}
    </div>
  );
}

export function Paper({
  children,
  elevation = 1,
  sx,
  style,
  className = "",
  ...resto
}) {
  // La elevación de MUI se aproxima con las sombras de Bootstrap.
  const sombra = elevation >= 3 ? "shadow" : elevation >= 1 ? "shadow-sm" : "";

  return (
    <div
      // text-dark es necesario: el tema del dashboard pone el color de texto
      // global en blanco y sobre el fondo blanco del Paper no se vería nada.
      className={`bg-white text-dark rounded ${sombra} ${className}`.trim()}
      style={{ ...sxAEstilo(sx), ...style }}
      {...resto}
    >
      {children}
    </div>
  );
}

export function Chip({ label, color = "secondary", className = "", ...resto }) {
  const tono = COLOR_A_BOOTSTRAP[color] || "secondary";

  return (
    <span className={`badge bg-${tono} ${className}`.trim()} {...resto}>
      {label}
    </span>
  );
}
